import type { FocusEventHandler } from 'react';
import type { TextAreaProps } from '@hdir/ui/text-area';
import type {
	FieldPath,
	FieldPathValue,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form';

import { isShowIfFunction, type ShowIfFunction } from './isShowIfFunction';

import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import {
	TextArea as FriskTextArea,
} from '@hdir/ui/text-area';
import React, { useEffect, useRef, useState } from 'react';
import {
	useController,
	useWatch,
} from 'react-hook-form';
import { useFormContext } from './FormContext';


export function TextArea<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	// @ts-expect-error defaultValue is Omittted in the type, but one could still pass it...
	defaultValue: _unused, // Make sure defaultValue doesn't end up in otherProps.
	disabled: disabledProp,
	// @ts-expect-error error is Omittted in the type, but one could still pass it...
	error: _hardcoded_inside1, // Make sure error doesn't end up in otherProps.
	label,
	errorSummaryLabel = isStringLiteral(label) ? label : undefined,
	name,
	onBlur: onBlurProp,
	// @ts-expect-error onChange is Omittted in the type, but one could still pass it...
	onChange: _gotten_from_usecontroller1, // Make sure onChange doesn't end up in otherProps.
	rules,
	showIf,
	// @ts-expect-error value is Omittted in the type, but one could still pass it...
	value: _gotten_from_usecontroller2, // Make sure value doesn't end up in otherProps.
	...otherProps
}: Omit<TextAreaProps,
	| 'defaultValue'
	| 'error'
	| 'name'
	| 'onBlur'
	| 'onChange'
	| 'value'
> & {
	errorSummaryLabel?: string;
	name: TName;
	onBlur?: FocusEventHandler<HTMLTextAreaElement|HTMLLabelElement> & FocusEventHandler<HTMLLabelElement>;
	rules?: UseControllerProps<TFieldValues, TName>['rules'];
	showIf?: ShowIfFunction<TFieldValues>;
}): React.JSX.Element | null {
	const {
		control,
		getRequired,
		setErrorSummaryLabel,
		setFocusRef,
		setScrollRef,
	} = useFormContext<TFieldValues>();
	const required: boolean = rules
		? !!rules.required
		: getRequired
			? getRequired(name)
			: false;
	const {
		field: {
			disabled: useControllerDisabled,
			onBlur: useControllerOnBlur,
			onChange: useControllerOnChange,
			// ref: useControllerFieldRef,
			value,
		},
		fieldState: {
			error,
		},
	} = useController<TFieldValues, TName>({
		control,
		disabled: disabledProp,
		name,
		rules,
	});
	const focusRef = useRef<HTMLTextAreaElement>();
	const scrollRef = useRef<HTMLDivElement>();
	const [hydrated, setHydrated] = useState(false);

	const disabled = !hydrated || useControllerDisabled;

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		if (errorSummaryLabel) {
			setErrorSummaryLabel({field: name, label: errorSummaryLabel});
		}
	},[errorSummaryLabel]);

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		setHydrated(true);
	}, []);

	useEffect(() => {
		if (focusRef.current) {
			setFocusRef({ field: name, el: focusRef.current });
		}
	}, [focusRef.current]);

	useEffect(() => {
		if (scrollRef.current && isStringLiteral(label)) {
			setScrollRef({ field: name, el: scrollRef.current });
		}
	}, [label, scrollRef.current]);

	if (isShowIfFunction(showIf) && !showIf({
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		watch: (watchParams) => useWatch({...watchParams, control}) as FieldPathValue<TFieldValues, FieldPath<TFieldValues>>,
	})) {
		return null;
	}

	return (
		<div
			ref={(el) => {
				if (el) {
					scrollRef.current = el;
				}
			}}
		>
			<FriskTextArea
				{...otherProps}

				// NOTE: This will always be a controlled component.
				// So defaultValue shouldn't be supported.
				// value will contain parent form defaultValues[name].

				disabled={disabled}
				error={error ? error.message : null}
				label={label}
				name={name}

				onBlur={(e) => {
					// https://react-hook-form.com/docs/usecontroller
					// A function which sends the input's onBlur event to the library.
					// It should be assigned to the input's onBlur prop.
					useControllerOnBlur();

					if (onBlurProp) onBlurProp(e);
				}}

				onChange={(e) => {
					useControllerOnChange(e);
				}}

				ref={(textAreaElOrNull) => {
					if (textAreaElOrNull) focusRef.current = textAreaElOrNull;
				}}
				required={required}
				value={value}
			/>
		</div>
	);
}
